import { Key } from "@unleashed/models/Key";
import {
  DocumentReplaceRequest,
  UpdateBrandsRequest,
  UpdateRolesRequest,
  UpdateViewableRequest,
  UploadRequest,
} from "../api/wingmanAdminAPI";
import {
  WingmanAdminDocument,
  WingmanAdminDocumentRequiredFields,
  WingmanAdminDocumentToReplace,
} from "../models/WingmanAdminDocument";

export const rolesMap = {
  staging: {
    "System Admin": "SuperAdmin",
    "Enterprise Support": "UBEE",
    "QA Test Role": "UBEE",
    "Migration": "Owner",
    "UA Franchise Owner": "Owner",
    "Franchise Owner": "Owner",
    "Sylvan Owner": "Owner",
    "Director": "Director",
    "Team (Inst)": "Instructor",
  },
  prod: {
    "System Administrator": "SuperAdmin",
    "Salesforce Mgmt": "SLT",
    "Finance": "SLT",
    "Enterprise Support": "UBEE",
    "Brand Support": "UBEE",
    "GLT Director": "UBEE",
    "GLT": "UBEE",
    "Migration Owner": "Owner",
    "Franchise Owner": "Owner",
    "Sylvan Owner": "Owner",
    "Franchise Owner (UA)": "Owner",
    "Director/Staff": "Director",
    "General Manager (UA)": "Director",
    "Staff": "Staff",
  },
};

export type CurrentEnvironment = "staging" | "prod";

type RoleKey =
  | "System Admin"
  | "Enterprise Support"
  | "QA Test Role"
  | "Migration"
  | "UA Franchise Owner"
  | "Franchise Owner"
  | "Director"
  | "Team (Inst)"
  | "System Administrator"
  | "Brand Support"
  | "Finance"
  | "Salesforce Mgmt"
  | "GLT Director"
  | "GLT"
  | "Migration Owner"
  | "Director/Staff"
  | "General Manager (UA)"
  | "Staff";

const roleRanking = [
  { roleName: "SuperAdmin", rank: 1 },
  { roleName: "Exec", rank: 2 },
  { roleName: "Staff", rank: 3 },
  { roleName: "SLT", rank: 4 },
  { roleName: "UBEE", rank: 5 },
  { roleName: "Director", rank: 6 },
  { roleName: "Instructor", rank: 7 },
  { roleName: "Owner", rank: 8 },
];

export const getRoleRank = (roleName: string): number => {
  const role = roleRanking.find((role) => role.roleName === roleName);
  return role ? role.rank : 0;
};

export const mapRole = (
  role: RoleKey | string,
  environment: CurrentEnvironment
): string => {
  return (
    rolesMap[environment][
      role as keyof (typeof rolesMap)[CurrentEnvironment]
    ] || "Staff"
  );
};

type BrandKey =
  | "all-brands"
  | "urban-air"
  | "snapology"
  | "the-little-gym"
  | "premier-martial-arts"
  | "class-101"
  | "sylvan-learning"
  | "water-wings";

export const brandsMap = {
  "all-brands": "UB",
  "urban-air": "UA",
  "snapology": "SNAP",
  "the-little-gym": "TLG",
  "premier-martial-arts": "PMA",
  "class-101": "C101",
  "sylvan-learning": "SYL",
  "water-wings": "WW",
};

export const mapBrand = (brand: BrandKey | string): string => {
  return brandsMap[brand as BrandKey] || "UB";
};

// Wingman Admin Helpers
export const checkIfAllFieldsAreFilled = (
  doc: WingmanAdminDocumentRequiredFields
) => {
  if (
    doc.Brand.length > 0 &&
    doc.Role.length > 0 &&
    doc.Viewable !== undefined
  ) {
    return "Ready";
  }
  return "Incomplete";
};

export const mapRoleBrandToStrArr = (
  initialDocuments: WingmanAdminDocument[]
) => {
  return initialDocuments.map((wingmanDocument: WingmanAdminDocument) => ({
    ...wingmanDocument,
    Role: Array.isArray(wingmanDocument.Role)
      ? wingmanDocument.Role
      : convertStringOptionsToArr(wingmanDocument.Role ?? ""),
    Brand: Array.isArray(wingmanDocument.Brand)
      ? wingmanDocument.Brand
      : convertStringOptionsToArr(wingmanDocument.Brand ?? ""),
  }));
};

export const convertStringOptionsToArr = (payload: string): string[] => {
  return payload.split(",");
};

export const createReplacePayload = (
  uploadedFileToReplace: WingmanAdminDocumentToReplace,
  file: File
) => {
  const payload: DocumentReplaceRequest = {
    file_name: uploadedFileToReplace.Filename,
    updated_file: file,
    viewables: [uploadedFileToReplace.Viewable],
    roles: uploadedFileToReplace.Role,
    brands: uploadedFileToReplace.Brand,
  };
  return payload;
};

export const createUploadPayload = (
  filesToImport: WingmanAdminDocument[],
  files: File[]
) => {
  const payload: UploadRequest = {
    files: [],
    viewables: [],
    roles: [],
    brands: [],
  };

  filesToImport.forEach((doc) => {
    payload.files.push(files.filter((f) => f.name === doc.Filename)[0]);

    payload.viewables.push(doc.Viewable === true);

    // Join brands into a comma-separated string ['brand1', 'brand2'] -> 'brand1,brand2'
    const brandsString = doc.Brand ? doc.Brand.join(",") : "";
    payload.brands.push(brandsString);

    // Join roles into a comma-separated string ['role1', 'role2'] -> 'role1,role2'
    const rolesString = doc.Role ? doc.Role.join(",") : "";
    payload.roles.push(rolesString);
  });

  return payload;
};

export const createUpdateRolesPayload = (
  fileToUpdate: WingmanAdminDocument
) => {
  const payload: UpdateRolesRequest = {
    file_name: fileToUpdate.Filename,
    roles: fileToUpdate.Role ? fileToUpdate.Role.join(",") : "",
  };

  return payload;
};

export const createUpdateBrandsPayload = (
  fileToUpdate: WingmanAdminDocument
) => {
  const payload: UpdateBrandsRequest = {
    file_name: fileToUpdate.Filename,
    brands: fileToUpdate.Brand ? fileToUpdate.Brand.join(",") : "",
  };
  return payload;
};

export const createUpdateViewablePayload = (
  fileToUpdate: WingmanAdminDocument
) => {
  if (typeof fileToUpdate.Viewable !== "boolean") {
    return undefined;
  }
 
  const payload: UpdateViewableRequest = {
    file_name: fileToUpdate.Filename,
    viewable: fileToUpdate.Viewable,
  };
  return payload;
};

export const canEdit = (
  authorizedBrands: string[],
  brandsToCheck: string[]
): boolean => {
  const normalizedAuthorizedBrands = authorizedBrands.map((b) =>
    b.toLowerCase()
  );
  const normalizedBrandsToCheck = brandsToCheck.map((b) => b.toLowerCase());

  if (normalizedAuthorizedBrands.includes("ub")) {
    return true;
  }
  return normalizedBrandsToCheck.every((brand) =>
    normalizedAuthorizedBrands.includes(brand)
  );
};

export const brand_options: Key<string>[] = [
  { id: "SNAP", name: "Snapology" },
  { id: "UA", name: "Urban Air" },
  { id: "TLG", name: "The Little Gym" },
  { id: "PMA", name: "Premier Martial Arts" },
  { id: "SYL", name: "Sylvan Learning" },
  { id: "UB", name: "All Brands" },
  { id: "C101", name: "Class 101" },
  { id: "WW", name: "Water Wings" },
];

export const brandsArrayForAdminDocumentsAPI: string[] = [
  "UB",
  "SNAP",
  "UA",
  "TLG",
  "PMA",
  "SYL",
  "C101",
];

export const role_options: Key<string>[] = [
  { id: "Owner", name: "Owner" },
  { id: "SuperAdmin", name: "SuperAdmin" },
  { id: "Director", name: "Director" },
  { id: "Staff", name: "Staff" },
  { id: "SLT", name: "SLT" },
  { id: "UBEE", name: "UBEE" },
  { id: "Intructor", name: "Instructor" },
];

export const removeExistingDocumentsFromPayload = (
  payload: UploadRequest,
  existingDocumentNames: string[]
): UploadRequest => {
  const filteredPayload: UploadRequest = {
    files: [],
    viewables: [],
    roles: [],
    brands: [],
  };

  for (let i = 0; i < payload.files.length; i++) {
    const file = payload.files[i];
    if (!existingDocumentNames.includes(file.name)) {
      filteredPayload.files.push(file);
      filteredPayload.viewables.push(payload.viewables[i]);
      filteredPayload.roles.push(payload.roles[i]);
      filteredPayload.brands.push(payload.brands[i]);
    }
  }

  return filteredPayload;
};


